import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSeoMeta } from '@unhead/react';
import { useBatchedDecks } from '@/hooks/useBatchedDecks';
import { DeckCard } from '@/components/DeckCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Swords, Library, Search, Layers } from 'lucide-react';
import { useMemo } from 'react';



const Index = () => {
  const { user } = useCurrentUser();
  const { data, isLoading } = useBatchedDecks();

  const latestDecks = data?.latestDecks;
  const followingDecks = data?.followingDecks;
  const myLists = data?.myLists;
  const [ctaIndex, setCtaIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const ctaItems = useMemo(
    () => [
      { text: 'Build a Deck', icon: <Swords className="mr-2" /> },
      { text: 'Start a Collection', icon: <Library className="mr-2" /> },
      { text: 'Search for Cards', icon: <Search className="mr-2" /> },
    ],
    []
  );

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCtaIndex(prevIndex => (prevIndex + 1) % ctaItems.length);
    }, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [ctaItems.length]);



  useSeoMeta({
    title: 'Surveil - Magic: The Gathering on Nostr',
    description: 'Search, collect, and share Magic: The Gathering cards via Scryfall API and Nostr.',
  });

  return (
    <div className="min-h-screen">
      <div className="relative w-full py-12 md:py-24 min-h-[calc(100vh-3rem)] md:min-h-[25rem] xl:min-h-[30rem] lg:py-0 flex items-center text-foreground overflow-hidden">
        {/* Mystical Background */}
        <div className="absolute inset-0 -z-10">
          {/* Base gradient - theme aware */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-black dark:from-slate-900 dark:via-blue-950 dark:to-black light:from-blue-50 light:via-blue-100 light:to-slate-100"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 text-center relative">
          {/* Mystical glow behind title */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[32rem] h-[32rem] bg-blue-500/15 rounded-full blur-3xl animate-mystical-glow dark:bg-blue-500/15 light:bg-blue-500/25"></div>

          <h1 className="text-5xl xl:text-6xl font-bold tracking-tight text-white drop-shadow-2xl relative">
            Surveil the Multiverse
          </h1>
          <p className="mt-4 md:mt-6 max-w-2xl mx-auto text-lg xl:text-xl text-slate-700 dark:text-blue-100/90 drop-shadow-lg relative">
            Create decks and share them across decentralized relays.<br></br>
            What shall you build today?
          </p>
          <div className="mt-8 text-sm text-slate-600 dark:text-blue-200/70 relative">
            Vibed with <a href="https://soapbox.pub/mkstack" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 dark:text-blue-400 dark:hover:text-blue-300 light:text-blue-600 light:hover:text-blue-700 transition-colors">MKStack</a>
            <span className="mx-2">|</span>
            Powered by{" "}
            <a
              href="https://scryfall.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 dark:text-blue-400 dark:hover:text-blue-300 light:text-blue-600 light:hover:text-blue-700 transition-colors"
            >
              Scryfall API
            </a>
          </div>

          <div className="mt-8">
            <div className="flex justify-center">
              <Button
                asChild
                size="lg"
                className="relative overflow-hidden font-bold text-lg px-8 py-6 w-80 h-16 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-lg shadow-lg transform hover:scale-105 transition-transform duration-300"
              >
                <Link to="/build" className="flex items-center justify-center">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={ctaIndex}
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -20, opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="flex items-center justify-center w-full text-center gap-2"
                    >
                      {ctaItems[ctaIndex].icon}
                      {ctaItems[ctaIndex].text}
                    </motion.div>
                  </AnimatePresence>
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
      <main className="flex-grow">
        <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-7xl mx-auto">
                {/* Latest Decks */}
                <div className="py-12">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-2xl font-semibold text-foreground">Latest Decks</h3>
                        <Button asChild variant="outline" size="sm">
                            <Link to="/browse">
                                <Layers className='w-4 h-4' />
                                Browse Decks
                            </Link>
                        </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
                        {isLoading ? (
                            <>
                                <Skeleton className="h-64" />
                                <Skeleton className="h-64" />
                                <Skeleton className="h-64" />
                            </>
                        ) : (
                            latestDecks?.map(deck => <DeckCard key={deck.event.id} deck={deck} />)
                        )}
                    </div>
                </div>

                {/* Following Decks */}
                {user && followingDecks && followingDecks.length > 0 && (
                    <div className="py-12">
                        <h3 className="text-2xl font-semibold text-foreground mb-4">Latest Decks from Following</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
                            {isLoading ? (
                                <>
                                    <Skeleton className="h-64" />
                                    <Skeleton className="h-64" />
                                    <Skeleton className="h-64" />
                                </>
                            ) : (
                                followingDecks.map(deck => <DeckCard key={deck.event.id} deck={deck} />)
                            )}
                        </div>
                    </div>
                )}

                {/* My Lists */}
                {user && myLists && myLists.length > 0 && (
                    <div className="py-12">
                        <h3 className="text-2xl font-semibold text-foreground mb-4">Your Latest Decks</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {isLoading ? (
                                <>
                                    <Skeleton className="h-64" />
                                    <Skeleton className="h-64" />
                                    <Skeleton className="h-64" />
                                </>
                            ) : (
                                myLists.slice(0, 3).map(deck => <DeckCard key={deck.event.id} deck={deck} />)
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
      </main>
    </div>
  );
};

export default Index;
